import ee from '../../services/earth-engine';
import { SPM_BAND, spmTrainingFilter } from './spm.js';

/**
 * Building the training sample of the tree models, and measuring them on data they
 * never saw.
 * Transposed from the reference Earth Engine script (Mascagni, 2026, sections 1.6.1 and 4.6). Three
 * of its decisions carry the weight here, and none of them is cosmetic:
 * - **The folds are blocks of ground, not random points.** Two pixels thirty metres apart see
 *   almost the same water over almost the same bottom, so a random split leaves the model being
 *   measured on a neighbour of what it was fitted on. Blocks of 1500 m break that; the reference
 *   notes that a larger block is "more pessimistic and more honest", which is the whole point.
 * - **Every point is validated exactly once.** Five folds, each in turn held out, so the error is
 *   measured over the whole survey rather than over whichever third a single split happened to
 *   keep - which matters when the survey has a few hundred usable pixels, not thousands.
 * - **The sample is stratified by depth.** A survey spends most of its soundings in the depth range
 *   the vessel worked, and a model fitted on that distribution is poor exactly where the data is
 *   thin, which is the deep end everyone cares about.
 */

/**
 * @constant FOLD_PROPERTY
 * @description Column holding which fold a sample belongs to.
 * @memberof module:Bathymetry
 */
export const FOLD_PROPERTY = 'fold';

/**
 * @constant DEPTH_CLASS_PROPERTY
 * @description Column holding the depth band a sample is stratified by.
 * @memberof module:Bathymetry
 */
export const DEPTH_CLASS_PROPERTY = 'zbin';

/**
 * @constant CV_SCHEMES
 * @description How the folds are drawn. `BLOCK` is the reference's recommendation and the only one
 * that accounts for spatial autocorrelation; `RANDOM` is there to show what ignoring it costs.
 * @memberof module:Bathymetry
 */
export const CV_SCHEMES = { BLOCK: 'BLOCK', RANDOM: 'RANDOM', NONE: 'NONE' };

/**
 * @constant ENSEMBLE_WEIGHTINGS
 * @description How the ensemble weight is chosen. `OPTIMAL` minimises the out-of-fold squared
 * error, `INV_MSE` splits it by each member's error, `EQUAL` is a plain mean.
 * @memberof module:Bathymetry
 */
export const ENSEMBLE_WEIGHTINGS = { OPTIMAL: 'OPTIMAL', INV_MSE: 'INV_MSE', EQUAL: 'EQUAL' };

/**
 * @constant CV_DEFAULTS
 * @description The reference's validation settings.
 * @memberof module:Bathymetry
 */
export const CV_DEFAULTS = {
  cvScheme: CV_SCHEMES.BLOCK,
  cvFolds: 5,
  cvBlockSizeM: 1500,
  stratifyBinM: 1,
  samplesPerBin: 1500,
  filterBySpm: false,
  minDepth: null,
  ensembleWeighting: ENSEMBLE_WEIGHTINGS.OPTIMAL,
};

/**
 * @function foldImage
 * @description Assigns every pixel of the analysis grid to a fold, by the square block of ground it
 * falls in.
 * The block indices are combined with two coprime multipliers before the modulo so that blocks
 * touching each other land in different folds; adding them directly would give neighbouring blocks
 * consecutive folds and put half of each boundary in the same fold as its neighbour.
 * @param {Object} options
 * @param {Number} options.scale - Pixel scale of the analysis grid, in metres.
 * @param {String} options.crs - CRS of the analysis grid.
 * @param {Number} options.blockSizeM - Block side, in metres.
 * @param {Number} options.folds - How many folds.
 * **Called by:
 * @see {stratifiedTrainingSamples}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Image` with the `fold` band.
 */
export const foldImage = ({ scale, crs, blockSizeM, folds }) => {
  const projection = ee.Projection(crs).atScale(scale);
  const coordinates = ee.Image.pixelCoordinates(projection);
  const blockX = coordinates.select('x').divide(blockSizeM).floor();
  const blockY = coordinates.select('y').divide(blockSizeM).floor();
  return blockX.multiply(7).add(blockY.multiply(11)).abs().mod(folds).toInt().rename(FOLD_PROPERTY);
};

/**
 * @function stratifiedTrainingSamples
 * @description One sample per pixel that has an observed depth, drawn evenly across depth bands and
 * carrying everything the fit and the validation need: the predictors, the depth, the coordinates
 * and the fold.
 * Drawn from the image rather than read at the survey points, because that is what allows the draw
 * to be balanced by depth: a point collection can only be filtered, not rebalanced.
 * @param {Object} options
 * @param {Object} options.predictorImage - The predictor stack, as an `ee.Image`.
 * @param {Array<String>} options.predictors - The predictor band names.
 * @param {Object} options.observedDepth - The rasterised survey, as an `ee.Image` band `or_depth`.
 * @param {Object} options.region - The area of interest.
 * @param {Number} options.scale - Pixel scale, in metres.
 * @param {String} options.crs - CRS of the analysis grid.
 * @param {Number} options.seed - Seed for the draw.
 * @param {Number} [options.depthLimit] - Light extinction depth; points below it are dropped.
 * @param {Object} options.config - Validation settings, as {@link CV_DEFAULTS} shapes them.
 * **Called by:
 * @see {ProcessingPage} - before fitting a tree model.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection`.
 */
export const stratifiedTrainingSamples = ({
  predictorImage,
  predictors,
  observedDepth,
  region,
  scale,
  crs,
  seed,
  depthLimit,
  config,
}) => {
  const projection = ee.Projection(crs).atScale(scale);
  const depthClass = observedDepth.divide(config.stratifyBinM).floor().max(0).toInt().rename(DEPTH_CLASS_PROPERTY);
  const coordinates = ee.Image.pixelCoordinates(projection).rename(['x', 'y']);
  const folds =
    config.cvScheme === CV_SCHEMES.BLOCK && config.cvFolds >= 2
      ? foldImage({ scale, crs, blockSizeM: config.cvBlockSizeM, folds: config.cvFolds })
      : ee.Image.constant(0).toInt().rename(FOLD_PROPERTY);

  const stack = ee
    .Image(predictorImage)
    .select(predictors)
    .addBands(observedDepth)
    .addBands(depthClass)
    .addBands(coordinates)
    .addBands(folds)
    .updateMask(observedDepth.mask());

  let samples = stack.stratifiedSample({
    numPoints: config.samplesPerBin,
    classBand: DEPTH_CLASS_PROPERTY,
    region,
    scale,
    projection,
    seed,
    tileScale: 16,
    geometries: false,
  });

  samples = ee.FeatureCollection(samples).filter(ee.Filter.notNull([...predictors, 'or_depth']));
  if (Number.isFinite(depthLimit)) samples = samples.filter(ee.Filter.lte('or_depth', depthLimit));
  if (config.filterBySpm && predictors.indexOf(SPM_BAND) !== -1) samples = samples.filter(spmTrainingFilter());
  if (config.minDepth !== null && config.minDepth !== undefined) {
    samples = samples.filter(ee.Filter.gte('or_depth', config.minDepth));
  }

  /**
   * A random scheme still needs a fold per sample; it is drawn here rather than from the grid, so
   * the difference against the block scheme is exactly the spatial structure and nothing else.
   */
  if (config.cvScheme === CV_SCHEMES.RANDOM && config.cvFolds >= 2) {
    samples = samples.randomColumn('cv_draw', seed).map((feature) =>
      ee
        .Feature(feature)
        .set(FOLD_PROPERTY, ee.Number(ee.Feature(feature).get('cv_draw')).multiply(config.cvFolds).floor().toInt()),
    );
  }

  return samples;
};

/**
 * @function memberField
 * @description Column each ensemble member writes its own prediction to.
 * @param {String} kind - The ensemble's name.
 * **Called by:
 * @see {crossValidate}
 * @see {withPredictedDepth}
 * @memberof module:Bathymetry
 * @returns {String}
 */
export const memberField = (kind) => (kind === 'Random Forest' ? 'pred_rf' : 'pred_gbt');

/**
 * @function crossValidate
 * @description Fits the model on every fold but one and predicts the one left out, in turn, so each
 * sample ends up carrying a prediction made without it.
 * Every member of an ensemble is run over the *same* folds, which is what makes the comparison
 * between them - and the weight drawn from it - mean anything.
 * @param {Object} samples - Training samples carrying {@link FOLD_PROPERTY}.
 * @param {Object} options
 * @param {Array<String>} options.kinds - The ensembles to fit.
 * @param {Array<Number>} options.folds - The fold values to hold out in turn. These are the folds
 *  that have samples, not a count: a block grid can leave a fold empty, and holding out an empty
 *  fold trains on everything and predicts nothing - see {@link populatedFolds}.
 * @param {Function} options.train - `(features, kind) => ee.Classifier`.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection` of out-of-fold predictions.
 */
export const crossValidate = (samples, { kinds, folds, train }) => {
  const collection = ee.FeatureCollection(samples);
  const parts = [];
  folds.forEach((fold) => {
    const fitting = collection.filter(ee.Filter.neq(FOLD_PROPERTY, fold));
    let held = collection.filter(ee.Filter.eq(FOLD_PROPERTY, fold));
    kinds.forEach((kind) => {
      held = held.classify(train(fitting, kind), memberField(kind));
    });
    parts.push(held);
  });
  return ee.FeatureCollection(parts).flatten();
};

/**
 * @function populatedFolds
 * @description Which folds actually hold samples, as a list of fold values with at least one
 * sample each and at least one sample outside them.
 * A spatial block grid does not promise a usable split. The blocks are laid over the whole scene,
 * while the soundings occupy whatever part of it the survey covered, so a survey narrower than the
 * block size lands in a single block and every sample draws the same fold. Cross-validating that
 * means training on the complement of everything, which is nothing, and Earth Engine answers from
 * inside the classifier with "No valid training data were found" - naming neither the fold nor the
 * block size. Counting first turns that into a decision this module can take and report.
 * @param {Object} histogram - `aggregate_histogram` of the fold column, already evaluated.
 * **Called by:
 * @see {ProcessingPage} - to decide whether the cross-validation can run at all.
 * @memberof module:Bathymetry
 * @returns {Array<Number>} The usable fold values, ascending. Fewer than two means the split is
 *  degenerate and there is no cross-validation to run.
 */
export const populatedFolds = (histogram) => {
  const counted = Object.entries(histogram ?? {})
    .map(([fold, size]) => [Number(fold), Number(size)])
    .filter(([fold, size]) => Number.isFinite(fold) && size > 0);
  if (counted.length < 2) return [];
  return counted.map(([fold]) => fold).sort((a, b) => a - b);
};

/**
 * @function ensembleWeightFromOutOfFold
 * @description The share of the ensemble that should come from the forest, chosen on predictions
 * neither member saw.
 * `OPTIMAL` is the weight that minimises the squared error of the combination, which has a closed
 * form; it is clamped to [0, 1] so the result stays a blend of the two rather than an extrapolation
 * beyond either. One parameter estimated on held-out data adds negligible optimism.
 * @param {Object} outOfFold - As {@link crossValidate} returns it.
 * @param {String} weighting - One of {@link ENSEMBLE_WEIGHTINGS}.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Number` between 0 and 1.
 */
export const ensembleWeightFromOutOfFold = (outOfFold, weighting) => {
  if (weighting === ENSEMBLE_WEIGHTINGS.EQUAL) return ee.Number(0.5);

  const errors = ee
    .FeatureCollection(outOfFold)
    .filter(ee.Filter.notNull(['or_depth', 'pred_rf', 'pred_gbt']))
    .map((feature) => {
      const row = ee.Feature(feature);
      const observed = ee.Number(row.get('or_depth'));
      const forestError = ee.Number(row.get('pred_rf')).subtract(observed);
      const boostError = ee.Number(row.get('pred_gbt')).subtract(observed);
      return row.set({
        ens_forest_sq: forestError.pow(2),
        ens_boost_sq: boostError.pow(2),
        ens_numerator: boostError.multiply(boostError.subtract(forestError)),
        ens_denominator: forestError.subtract(boostError).pow(2),
      });
    });

  if (weighting === ENSEMBLE_WEIGHTINGS.INV_MSE) {
    const forestMse = ee.Number(errors.aggregate_mean('ens_forest_sq'));
    const boostMse = ee.Number(errors.aggregate_mean('ens_boost_sq'));
    return boostMse.divide(forestMse.add(boostMse));
  }

  const numerator = ee.Number(errors.aggregate_sum('ens_numerator'));
  const denominator = ee.Number(errors.aggregate_sum('ens_denominator'));
  return ee.Number(
    ee.Algorithms.If(denominator.gt(0), numerator.divide(denominator).max(0).min(1), ee.Number(0.5)),
  );
};

/**
 * @function withPredictedDepth
 * @description Writes the combined prediction into `pred_depth`, so the out-of-fold table is read
 * by the same code that reads an ordinary result.
 * @param {Object} outOfFold - As {@link crossValidate} returns it.
 * @param {Array<String>} kinds - The ensembles that were fitted.
 * @param {Object} weight - The forest's share, as an `ee.Number`.
 * **Called by:
 * @see {ProcessingPage}
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection`.
 */
export const withPredictedDepth = (outOfFold, kinds, weight) => {
  const collection = ee.FeatureCollection(outOfFold);
  if (kinds.length === 1) {
    const only = memberField(kinds[0]);
    return collection.map((feature) => ee.Feature(feature).set('pred_depth', ee.Feature(feature).get(only)));
  }
  const forestShare = ee.Number(weight);
  return collection.map((feature) => {
    const row = ee.Feature(feature);
    const blended = ee
      .Number(row.get('pred_rf'))
      .multiply(forestShare)
      .add(ee.Number(row.get('pred_gbt')).multiply(ee.Number(1).subtract(forestShare)));
    return row.set('pred_depth', blended);
  });
};

/**
 * @function withResiduals
 * @description Adds the signed, absolute and squared residual of every row, which is the shape the
 * error reports read.
 * @param {Object} collection - Rows carrying the observed and predicted depth.
 * @param {String} [observedField]
 * @param {String} [predictedField]
 * **Called by:
 * @see {metricsOf}
 * @see {ProcessingPage} - before breaking the error down by depth band.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.FeatureCollection`.
 */
export const withResiduals = (collection, observedField = 'or_depth', predictedField = 'pred_depth') =>
  ee
    .FeatureCollection(collection)
    .filter(ee.Filter.notNull([observedField, predictedField]))
    .map((feature) => {
      const row = ee.Feature(feature);
      const observed = ee.Number(row.get(observedField));
      const difference = ee.Number(row.get(predictedField)).subtract(observed);
      return row.setMulti({ diff: difference, absdiff: difference.abs(), sqdiff: difference.pow(2), o: observed });
    });

/**
 * @function metricsOf
 * @description Sample count, RMSE, MAE, bias and R² over a collection of predictions.
 * @param {Object} collection - Rows carrying the observed and predicted depth.
 * @param {String} [observedField]
 * @param {String} [predictedField]
 * **Called by:
 * @see {ProcessingPage} - reporting the out-of-fold error.
 * @memberof module:Bathymetry
 * @returns {Object} An `ee.Dictionary`.
 */
export const metricsOf = (collection, observedField = 'or_depth', predictedField = 'pred_depth') => {
  const rows = withResiduals(collection, observedField, predictedField);
  const count = rows.size();
  const meanObserved = ee.Number(rows.aggregate_mean('o'));
  const totalSquares = ee.Number(
    rows.map((row) => ee.Feature(row).set('sst', ee.Number(ee.Feature(row).get('o')).subtract(meanObserved).pow(2))).aggregate_sum('sst'),
  );
  const residualSquares = ee.Number(rows.aggregate_sum('sqdiff'));

  return ee.Dictionary({
    n: count,
    rmse: ee.Number(rows.aggregate_mean('sqdiff')).sqrt(),
    mae: ee.Number(rows.aggregate_mean('absdiff')),
    bias: ee.Number(rows.aggregate_mean('diff')),
    r2: ee.Algorithms.If(totalSquares.eq(0), null, ee.Number(1).subtract(residualSquares.divide(totalSquares))),
  });
};
